import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiGlobe,
  FiUsers,
  FiTrendingUp,
  FiRefreshCw,
  FiAlertCircle,
} from "react-icons/fi";
import { getAllAssociations } from "../../../../services/associationsServices";

const DATA_PATHS = {
  visits: "/api/visites/countVisitesByDate",
  totalUsers: "/api/users/countUsers",
  todayUsers: "/api/users/countUsersToday",
};

const MAIRIE_KEYS = ["MONAPPLIASSO"];

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(Number(value || 0));
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function getNumericValue(data) {
  if (typeof data === "number") return data;
  if (typeof data?.count === "number") return data.count;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.value === "number") return data.value;
  return 0;
}

function getTodayVisits(data, today) {
  if (!Array.isArray(data)) return 0;

  const row = data.find((item) => {
    const dateValue = item?.date_visite || item?.date || item?.jour || "";
    return String(dateValue).startsWith(today);
  });

  return Number(row?.compteur ?? row?.count ?? row?.total ?? 0);
}

function isMairieAssociation(asso) {
  const product = String(asso?.produit_asso || "").toUpperCase();
  const name = String(asso?.nom_asso || "").toUpperCase();

  return MAIRIE_KEYS.some(
    (key) => product.includes(key) || name.includes(key)
  );
}

function MiniStatCard({ icon, label, value, accent = "blue" }) {
  const accents = {
    blue: "from-sky-500/15 to-cyan-500/10 text-sky-700",
    emerald: "from-emerald-500/15 to-green-500/10 text-emerald-700",
    violet: "from-violet-500/15 to-fuchsia-500/10 text-violet-700",
    amber: "from-amber-500/15 to-orange-500/10 text-amber-700",
  };

  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </div>
          <div className="mt-2 text-2xl font-extrabold text-[#123B57]">
            {value}
          </div>
        </div>

        <div
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br",
            accents[accent]
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function LogoFallback({ name }) {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#EEF7FC] font-bold text-[#123B57]">
      {getInitials(name)}
    </div>
  );
}

const MairieSummaryTable = () => {
  const [mairieData, setMairieData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMairieData = useCallback(async () => {
    setLoading(true);
    const today = new Date().toISOString().split("T")[0];

    try {
      const allAssociations = await getAllAssociations();

      const filtered = (allAssociations || []).filter(
        (asso) =>
          isMairieAssociation(asso) &&
          !!asso.apiUrl_asso &&
          String(asso.apiUrl_asso).startsWith("http")
      );

      const results = await Promise.all(
        filtered.map(async (asso) => {
          const app = {
            id: asso.id,
            name: asso.nom_asso,
            logo: asso.logo_asso,
            appUrl: asso.siteweb_asso,
            apiUrl: asso.apiUrl_asso,
          };

          const result = {
            ...app,
            visits: 0,
            totalUsers: 0,
            todayUsers: 0,
            error: false,
          };

          try {
            const [visitsRes, totalUsersRes, todayUsersRes] =
              await Promise.allSettled([
                axios.get(`${app.apiUrl}${DATA_PATHS.visits}`),
                axios.get(`${app.apiUrl}${DATA_PATHS.totalUsers}`),
                axios.get(`${app.apiUrl}${DATA_PATHS.todayUsers}`),
              ]);

            if (visitsRes.status === "fulfilled") {
              result.visits = getTodayVisits(visitsRes.value.data, today);
            } else {
              result.error = true;
            }

            if (totalUsersRes.status === "fulfilled") {
              result.totalUsers = getNumericValue(totalUsersRes.value.data);
            } else {
              result.error = true;
            }

            if (todayUsersRes.status === "fulfilled") {
              result.todayUsers = getNumericValue(todayUsersRes.value.data);
            } else {
              result.error = true;
            }
          } catch (error) {
            console.error(`Erreur pour ${app.name}:`, error.message);
            result.error = true;
          }

          return result;
        })
      );

      results.sort((a, b) => {
        const visitsDiff = (b.visits || 0) - (a.visits || 0);
        if (visitsDiff !== 0) return visitsDiff;
        return (b.totalUsers || 0) - (a.totalUsers || 0);
      });

      setMairieData(results);
    } catch (error) {
      console.error("Erreur lors du chargement MonAppliMairie:", error);
      setMairieData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMairieData();
  }, [fetchMairieData]);

  const totals = useMemo(() => {
    return mairieData.reduce(
      (acc, app) => {
        acc.sites += 1;
        acc.visits += Number(app.visits) || 0;
        acc.totalUsers += Number(app.totalUsers) || 0;
        acc.todayUsers += Number(app.todayUsers) || 0;
        acc.errors += app.error ? 1 : 0;
        return acc;
      },
      {
        sites: 0,
        visits: 0,
        totalUsers: 0,
        todayUsers: 0,
        errors: 0,
      }
    );
  }, [mairieData]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

        <button
          type="button"
          onClick={fetchMairieData}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FiRefreshCw className={cn(loading && "animate-spin")} />
          Actualiser
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MiniStatCard
          icon={<FiGlobe size={20} />}
          label="Sites"
          value={formatNumber(totals.sites)}
          accent="blue"
        />
        <MiniStatCard
          icon={<FiTrendingUp size={20} />}
          label="Visites du jour"
          value={formatNumber(totals.visits)}
          accent="emerald"
        />
        <MiniStatCard
          icon={<FiUsers size={20} />}
          label="Utilisateurs"
          value={formatNumber(totals.totalUsers)}
          accent="violet"
        />
        <MiniStatCard
          icon={<FiUsers size={20} />}
          label="Nouveaux aujourd’hui"
          value={formatNumber(totals.todayUsers)}
          accent="amber"
        />
      </div>

      {loading ? (
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
          Chargement des statistiques MonAppliMairie...
        </div>
      ) : mairieData.length === 0 ? (
        <div className="rounded-[28px] border border-dashed border-slate-300 bg-[#F8FBFD] p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#123B57] shadow-sm">
            <FiAlertCircle size={24} />
          </div>
          <h4 className="mt-4 text-lg font-bold text-[#123B57]">
            Aucune instance MonAppliMairie trouvée
          </h4>
          <p className="mt-2 text-sm text-slate-500">
            Vérifie la valeur de <code>produit_asso</code> dans tes associations
            ou adapte la fonction <code>isMairieAssociation()</code>.
          </p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm lg:block">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                    <th className="px-6 py-4">Produit</th>
                    <th className="px-6 py-4">Visites du jour</th>
                    <th className="px-6 py-4">Utilisateurs</th>
                    <th className="px-6 py-4">Nouveaux</th>
                    <th className="px-6 py-4">État API</th>
                  </tr>
                </thead>

                <tbody>
                  {mairieData.map((app, index) => (
                    <tr
                      key={app.id || `${app.name}-${index}`}
                      className="border-t border-slate-100"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {app.logo ? (
                            <img
                              src={app.logo}
                              alt={app.name}
                              className="h-11 w-11 rounded-2xl object-cover ring-1 ring-slate-200"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <LogoFallback name={app.name} />
                          )}

                          <div className="min-w-0">
                            {app.appUrl ? (
                              <a
                                href={app.appUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block truncate text-sm font-bold text-[#123B57] hover:underline"
                              >
                                {app.name}
                              </a>
                            ) : (
                              <div className="block truncate text-sm font-bold text-[#123B57]">
                                {app.name}
                              </div>
                            )}

                            <div className="mt-1 text-xs text-slate-500">
                              {app.appUrl || "URL non renseignée"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm font-semibold text-slate-700">
                        {formatNumber(app.visits)}
                      </td>

                      <td className="px-6 py-4 text-sm font-semibold text-slate-700">
                        {formatNumber(app.totalUsers)}
                      </td>

                      <td className="px-6 py-4">
                        {app.todayUsers > 0 ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                            +{formatNumber(app.todayUsers)}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">0</span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-3 py-1 text-xs font-bold",
                            app.error
                              ? "bg-amber-100 text-amber-700"
                              : "bg-emerald-100 text-emerald-700"
                          )}
                        >
                          {app.error ? "Partiel" : "OK"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot className="bg-slate-50">
                  <tr className="border-t border-slate-200 text-sm font-extrabold text-[#123B57]">
                    <td className="px-6 py-4">Total</td>
                    <td className="px-6 py-4">{formatNumber(totals.visits)}</td>
                    <td className="px-6 py-4">
                      {formatNumber(totals.totalUsers)}
                    </td>
                    <td className="px-6 py-4">
                      +{formatNumber(totals.todayUsers)}
                    </td>
                    <td className="px-6 py-4">
                      {totals.errors > 0 ? `${totals.errors} partiel(s)` : "OK"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:hidden">
            {mairieData.map((app, index) => (
              <article
                key={app.id || `${app.name}-${index}`}
                className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  {app.logo ? (
                    <img
                      src={app.logo}
                      alt={app.name}
                      className="h-12 w-12 rounded-2xl object-cover ring-1 ring-slate-200"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <LogoFallback name={app.name} />
                  )}

                  <div className="min-w-0 flex-1">
                    {app.appUrl ? (
                      <a
                        href={app.appUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block truncate text-base font-extrabold text-[#123B57]"
                      >
                        {app.name}
                      </a>
                    ) : (
                      <div className="block truncate text-base font-extrabold text-[#123B57]">
                        {app.name}
                      </div>
                    )}

                    <div className="mt-1 text-xs text-slate-500">
                      {app.appUrl || "URL non renseignée"}
                    </div>
                  </div>

                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold",
                      app.error
                        ? "bg-amber-100 text-amber-700"
                        : "bg-emerald-100 text-emerald-700"
                    )}
                  >
                    {app.error ? "Partiel" : "OK"}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="rounded-2xl bg-slate-50 p-3 text-center">
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                      Visites
                    </div>
                    <div className="mt-1 text-lg font-extrabold text-[#123B57]">
                      {formatNumber(app.visits)}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-3 text-center">
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                      Utilisateurs
                    </div>
                    <div className="mt-1 text-lg font-extrabold text-[#123B57]">
                      {formatNumber(app.totalUsers)}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-3 text-center">
                    <div className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                      Nouveaux
                    </div>
                    <div className="mt-1 text-lg font-extrabold text-emerald-700">
                      +{formatNumber(app.todayUsers)}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MairieSummaryTable;